import { useEffect, useSyncExternalStore } from "react";
import { nextApi, type AutomationCatalogDto } from "../../api/nextApi";
import { EVENTS } from "../events";

/**
 * automationsStore — shared state of the "אוטומציות" button (badge per screen) and its panel.
 * The catalog is loaded once per session; counts refresh on changes, every 5 minutes and on focus.
 */
type State = {
  catalog: AutomationCatalogDto | null;
  counts: Record<string, number> | null;
  errors: number;
  error: string | null;
};

let state: State = { catalog: null, counts: null, errors: 0, error: null };
const listeners = new Set<() => void>();
const set = (patch: Partial<State>) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const snapshot = () => state;

let catalogP: Promise<AutomationCatalogDto | null> | null = null;
export function loadCatalog(force = false): Promise<AutomationCatalogDto | null> {
  if (state.catalog && !force) return Promise.resolve(state.catalog);
  if (catalogP && !force) return catalogP;
  catalogP = nextApi.automations
    .catalog()
    .then((c) => {
      set({ catalog: c, error: null });
      return c;
    })
    .catch((e: unknown) => {
      set({ error: (e as { message?: string })?.message || "לא הצלחנו לטעון את האוטומציות" });
      return null;
    })
    .finally(() => {
      catalogP = null;
    });
  return catalogP;
}

export async function loadCounts() {
  try {
    const res = await nextApi.automations.counts();
    set({ counts: res.counts, errors: res.errors });
  } catch {
    /* badge is best-effort */
  }
}

let pollers = 0;
let stop: (() => void) | null = null;
function startPolling() {
  pollers += 1;
  void loadCounts();
  if (pollers > 1) return;
  const t = window.setInterval(() => void loadCounts(), 5 * 60 * 1000);
  const on = () => void loadCounts();
  window.addEventListener(EVENTS.automationsChanged, on);
  window.addEventListener("focus", on);
  stop = () => {
    window.clearInterval(t);
    window.removeEventListener(EVENTS.automationsChanged, on);
    window.removeEventListener("focus", on);
  };
}
function stopPolling() {
  pollers = Math.max(0, pollers - 1);
  if (!pollers && stop) {
    stop();
    stop = null;
  }
}

export function useAutomationsState(enabled: boolean): State {
  const snap = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    if (!enabled) return undefined;
    startPolling();
    void loadCatalog();
    return stopPolling;
  }, [enabled]);
  return snap;
}

/** same rule as the server (automationCatalog.screenKeyForPath) — longest matching prefix */
export function screenKeyForPath(pathname: string, screens: AutomationCatalogDto["screens"] | undefined): string {
  const p = String(pathname || "").split("?")[0].replace(/\/+$/u, "") || "/";
  let best = "general";
  let bestLen = -1;
  (screens || []).forEach((s) => {
    s.paths.forEach((prefix) => {
      const hit = prefix === "/" ? p === "/" : p === prefix || p.startsWith(`${prefix}/`);
      if (hit && prefix.length > bestLen) {
        best = s.key;
        bestLen = prefix.length;
      }
    });
  });
  return best;
}

export const STATUS_META: Record<string, { label: string; tone: "success" | "neutral" | "danger" | "info" }> = {
  active: { label: "פעילה", tone: "success" },
  paused: { label: "מושהית", tone: "neutral" },
  error: { label: "נכשלה", tone: "danger" },
};

export const RUN_STATUS_META: Record<string, { label: string; tone: "info" | "success" | "warning" | "danger" | "neutral" }> = {
  running: { label: "רצה", tone: "info" },
  success: { label: "הצליחה", tone: "success" },
  partial: { label: "חלקית", tone: "warning" },
  failed: { label: "נכשלה", tone: "danger" },
  skipped: { label: "לא בוצע", tone: "neutral" },
};

export const ACTION_ICON: Record<string, string> = { send_email: "mail", create_task: "task", send_report: "chart" };

export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const time = d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === now.toDateString()) return `היום ${time}`;
  if (new Date(now.getTime() + 86400000).toDateString() === d.toDateString()) return `מחר ${time}`;
  if (new Date(now.getTime() - 86400000).toDateString() === d.toDateString()) return `אתמול ${time}`;
  return `${d.toLocaleDateString("he-IL", { weekday: "short", day: "numeric", month: "numeric" })} ${time}`;
}

/** "3 מיילים נשלחו · 2 משימות נוצרו" */
export function describeRun(summary: {
  emailsSent?: number;
  tasksCreated?: number;
  reportsSent?: number;
  matched?: number;
  skippedDone?: number;
  skippedNoRecipient?: number;
  dryRun?: boolean;
  errors?: string[];
}): string {
  const parts: string[] = [];
  const n = (v: number | undefined) => Number(v || 0).toLocaleString("he-IL");
  if (summary.dryRun) {
    return summary.matched ? `בדיקה: ${n(summary.matched)} רשומות מתאימות — לא בוצעה פעולה` : "בדיקה: אין רשומות מתאימות כרגע";
  }
  if (summary.emailsSent) parts.push(`${n(summary.emailsSent)} מיילים נשלחו`);
  if (summary.tasksCreated) parts.push(`${n(summary.tasksCreated)} משימות נוצרו`);
  if (summary.reportsSent) parts.push(`${n(summary.reportsSent)} דוחות נשלחו`);
  if (summary.skippedDone) parts.push(`${n(summary.skippedDone)} כבר טופלו קודם`);
  if (summary.skippedNoRecipient) parts.push(`${n(summary.skippedNoRecipient)} בלי נמען`);
  if (!parts.length) return summary.matched === 0 ? "אין רשומות מתאימות" : "לא בוצעה פעולה";
  return parts.join(" · ");
}
